/**
 * Header Component
 *
 * Top header bar with logo, global search, care recipient selector, and user menu.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md Section 2.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, User, Settings, LogOut, Menu, Users, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuButton } from '../../hooks/useMenuButton';
import { JoinCareTeamModal } from '../casefile';

interface CaseFileOption {
  id: string;
  name: string;
}

interface HeaderProps {
  caseFiles?: CaseFileOption[];
  activeCaseFileId?: string;
  onCaseFileChange?: (caseFileId: string) => void;
  onMenuClick?: () => void;
  showMobileMenu?: boolean;
  onJoinedCareTeam?: (caseFileId: string, caseFileName: string) => void;
}

export function Header({
  caseFiles = [],
  activeCaseFileId,
  onCaseFileChange,
  onMenuClick,
  showMobileMenu = false,
  onJoinedCareTeam,
}: HeaderProps): JSX.Element {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const careMenu = useMenuButton();
  const userMenu = useMenuButton();
  const [showJoinCareTeamModal, setShowJoinCareTeamModal] = useState(false);

  const activeCaseFile = caseFiles.find((cf) => cf.id === activeCaseFileId);
  const userDisplayName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const userInitial = userDisplayName.charAt(0).toUpperCase();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleCaseFileSelect = (caseFileId: string) => {
    onCaseFileChange?.(caseFileId);
    careMenu.close(true);
  };

  return (
    <header className="bg-white border-b border-soft-neutral h-16 flex items-center px-4 lg:px-6">
      {/* Mobile menu button */}
      {showMobileMenu && (
        <button
          onClick={onMenuClick}
          aria-label="Open menu"
          // P2-12: 44px min touch target (primary mobile nav; hit area only).
          className="lg:hidden mr-2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg hover:bg-soft-neutral transition-colors"
        >
          <Menu className="w-5 h-5 text-slate-ink" aria-hidden="true" />
        </button>
      )}

      {/* Left: Logo (only shown when sidebar is hidden on mobile) */}
      <div className="lg:hidden flex items-center gap-2 mr-4">
        <div className="w-8 h-8 bg-evergreen rounded-lg flex items-center justify-center">
          <span className="text-white font-display font-semibold text-lg">H</span>
        </div>
        <span className="font-display text-xl text-deep-forest font-light hidden sm:block">
          Hesvara
        </span>
      </div>

      {/* Center: Global Search */}
      <div className="flex-1 max-w-2xl mx-auto px-4">
        <form onSubmit={handleSearch} className="relative" role="search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search records, documents, and deadlines"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search records, documents, deadlines..."
            className="input pl-10 py-2 text-sm"
          />
        </form>
      </div>

      {/* Right: Care Recipient Selector + User Menu */}
      <div className="flex items-center gap-3">
        {/* Care Recipient Selector */}
        {caseFiles.length > 0 && (
          <div className="relative hidden sm:block">
            <button
              {...careMenu.triggerProps}
              onClick={careMenu.toggle}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-soft-neutral transition-colors text-sm"
            >
              <span className="text-muted">Caring for:</span>
              <span className="font-medium text-ink">
                {activeCaseFile?.name || 'Select...'}
              </span>
              <ChevronDown className="w-4 h-4 text-muted" aria-hidden="true" />
            </button>

            {careMenu.isOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden="true"
                  onClick={() => careMenu.close(true)}
                />
                <div
                  {...careMenu.menuProps}
                  aria-label="Select care recipient"
                  className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-soft-neutral py-1 z-20"
                >
                  {caseFiles.map((cf) => (
                    <button
                      key={cf.id}
                      role="menuitem"
                      onClick={() => handleCaseFileSelect(cf.id)}
                      className={`
                        w-full text-left px-4 py-2 text-sm transition-colors
                        ${cf.id === activeCaseFileId
                          ? 'bg-evergreen/10 text-evergreen'
                          : 'hover:bg-soft-neutral text-ink'
                        }
                      `}
                    >
                      {cf.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* User Menu */}
        <div className="relative">
          <button
            {...userMenu.triggerProps}
            onClick={userMenu.toggle}
            aria-label={`User menu for ${userDisplayName}`}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-soft-neutral transition-colors"
          >
            <div className="w-8 h-8 bg-evergreen/10 rounded-full flex items-center justify-center">
              <span className="text-evergreen font-semibold text-sm" aria-hidden="true">{userInitial}</span>
            </div>
            <span className="hidden md:block text-sm font-medium text-ink">
              {userDisplayName}
            </span>
            <ChevronDown className="w-4 h-4 text-muted hidden md:block" aria-hidden="true" />
          </button>

          {userMenu.isOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                aria-hidden="true"
                onClick={() => userMenu.close(true)}
              />
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-soft-neutral py-1 z-20">
                {/* Static account header — kept OUTSIDE role="menu" so the menu owns only menuitems (ARIA menu ownership). */}
                <div className="px-4 py-2 border-b border-soft-neutral">
                  <p className="text-sm font-medium text-ink">{userDisplayName}</p>
                  <p className="text-xs text-muted">{user?.email}</p>
                </div>

                <div {...userMenu.menuProps} aria-label="User menu">
                <button
                  role="menuitem"
                  onClick={() => {
                    userMenu.close(true);
                    navigate('/profile');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-soft-neutral transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4" aria-hidden="true" />
                  Profile
                </button>

                <button
                  role="menuitem"
                  onClick={() => {
                    userMenu.close(true);
                    setShowJoinCareTeamModal(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-soft-neutral transition-colors flex items-center gap-2"
                >
                  <Users className="w-4 h-4" aria-hidden="true" />
                  Join Care Team
                </button>

                <button
                  role="menuitem"
                  onClick={() => {
                    userMenu.close(true);
                    navigate('/settings');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-soft-neutral transition-colors flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" aria-hidden="true" />
                  Settings
                </button>

                <button
                  role="menuitem"
                  onClick={() => {
                    userMenu.close(true);
                    navigate('/privacy');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-soft-neutral transition-colors flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" aria-hidden="true" />
                  Data Privacy Policy
                </button>

                <div role="separator" className="border-t border-soft-neutral my-1" />
                <button
                  role="menuitem"
                  onClick={() => {
                    userMenu.close(false);
                    signOut();
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                  Sign Out
                </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Join Care Team Modal */}
      <JoinCareTeamModal
        isOpen={showJoinCareTeamModal}
        onClose={() => setShowJoinCareTeamModal(false)}
        onJoined={(caseFileId, caseFileName) => {
          setShowJoinCareTeamModal(false);
          onJoinedCareTeam?.(caseFileId, caseFileName);
        }}
      />
    </header>
  );
}

export default Header;
